import { apiClient } from 'utils/apiClient';
import { RequestHeaderKeyTypes, RequestHeaderValueTypes } from 'constants/RequestHeaderTypes';
import { ApiEndpoints } from 'models/apiEndpoints';
import { isResponseError } from 'models/apiClient';
import { downscaleAttachmentIfRequired } from 'utils/image';
import { EMIMETypes } from 'constants/MIMETypes';
import { EFileType } from 'constants/FileType';

const downloadAttachment = async (
  firearmId: string,
  attachmentId: string,
  fileName: string,
  fileType: string,
  openBrowser: boolean = false,
): Promise<Blob | null> => {
  const { data } = await apiClient.get(
    ApiEndpoints.DOWNLOAD_FIREARM_ATTACHMENT(firearmId, attachmentId),
  );
  if (isResponseError(data)) {
    return null;
  }
  const response = await fetch(data.url);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  if (openBrowser) {
    window.open(blobUrl, '_blank');
  } else {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', `${fileName}.${fileType.toLowerCase()}`);
    document.body.appendChild(link);
    link.click();
  }

  return data;
};

const deleteAttachment = async (firearmId: string, attachmentId: string): Promise<any> => {
  const res = await apiClient.delete(
    `${ApiEndpoints.DELETE_FIREARM_ATTACHMENT(firearmId, attachmentId)}`,
  );

  if (isResponseError(res.data)) {
    return null;
  }
  return res;
};

const previewAttachment = async (firearmId: string, attachmentId: string): Promise<any> => {
  const { data } = await apiClient.get(
    ApiEndpoints.DOWNLOAD_FIREARM_ATTACHMENT(firearmId, attachmentId),
  );

  if (isResponseError(data)) {
    return null;
  }
  const response = await fetch(data.url);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  return blobUrl;
};

const getType = (fileType: string) => {
  switch (fileType) {
    case EMIMETypes.JPG:
      return EFileType.JPG;
    case EMIMETypes.JPEG:
      return EFileType.JPEG;
    case EMIMETypes.PNG:
      return EFileType.PNG;
    case EMIMETypes.PDF:
      return EFileType.PDF;
    case EMIMETypes.XLSX:
      return EFileType.XLSX;
    default:
      return '';
  }
};

const addAttachment = async (firearmId: string, data: any): Promise<any> => {
  const { attachment, attachmentName } = data;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const file: any = await downscaleAttachmentIfRequired(attachment);
  const endpoint = data.transactionUniqueId
    ? `${ApiEndpoints.ADD_FIREARM_ATTACHMENT_IN_TRANSACTION(firearmId)}?transactionUniqueId=${
        data.transactionUniqueId
      }`
    : `${ApiEndpoints.ADD_FIREARM_ATTACHMENT(firearmId)}`;
  const formData = new FormData();
  formData.append('multipartFile', file, `${attachmentName}.${getType(attachment?.type)}`);
  const res = await apiClient.post(endpoint, formData, config);

  if (isResponseError(res.data)) {
    return null;
  }
  return res;
};

const addAncillaryAttachment = async (ancillaryId: string, data: any): Promise<any> => {
  const { attachment, attachmentName } = data;
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const file: any = await downscaleAttachmentIfRequired(attachment);
  const endpoint = data.transactionUniqueId
    ? `${ApiEndpoints.ADD_ACCESSORY_ATTACHMENTS_IN_TRANSACTION(
        ancillaryId,
      )}?transactionUniqueId=${data.transactionUniqueId}`
    : `${ApiEndpoints.ADD_ACCESSORY_ATTACHMENTS(ancillaryId)}`;
  const formData = new FormData();
  formData.append('multipartFile', file, `${attachmentName}.${getType(attachment?.type)}`);
  const res = await apiClient.post(endpoint, formData, config);

  if (isResponseError(res.data)) {
    return null;
  }
  return res;
};

const addLegalEntityAttachment = async (legalEntityId: string, data: any): Promise<any> => {
  const { attachment, attachmentName, attachmentDocumentTypeUniqueId } = data;
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const file: any = await downscaleAttachmentIfRequired(attachment);
  const endpoint = ApiEndpoints.ADD_LEGAL_ENTITY_ATTACHMENT(legalEntityId);
  const formData = new FormData();

  const parameters = {
    attachmentDocumentTypeUniqueId: attachmentDocumentTypeUniqueId || '',
    fileName: attachmentName,
  };
  formData.append(
    'parameters',
    new Blob([JSON.stringify(parameters)], {
      type: 'application/json',
    }),
  );

  formData.append('file', file, `${attachmentName}.${getType(attachment?.type)}`);

  const res = await apiClient.post(endpoint, formData, config);

  if (isResponseError(res.data)) {
    return null;
  }
  return res;
};

const previewAncillaryAttachment = async (
  ancillaryId: string,
  attachmentId: string,
): Promise<any> => {
  const { data } = await apiClient.get(
    ApiEndpoints.GET_ANCILLARY_ATTACHMENT(ancillaryId, attachmentId),
    {
      responseType: 'arraybuffer',
      headers: {
        [RequestHeaderKeyTypes.RESPONSE_TYPE]: RequestHeaderValueTypes.BLOB,
      },
    },
  );

  if (isResponseError(data)) {
    return null;
  }

  const blob = new Blob([data], { type: 'pdf' });
  return window.URL.createObjectURL(blob);
};

const downloadAncillaryAttachment = async (
  ancillaryId: string,
  attachmentId: string,
  fileName: string,
  fileType: string,
): Promise<Blob | null> => {
  const { data } = await apiClient.get(
    ApiEndpoints.GET_ANCILLARY_ATTACHMENT(ancillaryId, attachmentId),
    {
      responseType: 'arraybuffer',
      headers: {
        [RequestHeaderKeyTypes.RESPONSE_TYPE]: RequestHeaderValueTypes.BLOB,
      },
    },
  );
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}.${fileType.toLowerCase()}`);
  document.body.appendChild(link);
  link.click();

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const deleteAncillaryAttachment = async (
  ancillaryId: string,
  attachmentId: string,
): Promise<any> => {
  const res = await apiClient.delete(
    `${ApiEndpoints.DLETE_ANCILLARY_ATTACHMENT(ancillaryId, attachmentId)}`,
  );

  if (isResponseError(res.data)) {
    return null;
  }
  return res;
};

const previewLegalEntityAttachment = async (
  legalUniqueId: string,
  attachmentUniqueId: string,
): Promise<any> => {
  const response = await apiClient.get(
    ApiEndpoints.PREVIEW_LEGAL_ENTITY_ATTACHMENT(legalUniqueId, attachmentUniqueId),
  );

  if (response.status !== 200) {
    return null;
  }
  return response.data.url;
};

const downloadLegalEntityAttachment = async (
  legalUniqueId: string,
  attachmentUniqueId: string,
): Promise<any> => {
  const response = await apiClient.get(
    ApiEndpoints.DOWNLOAD_LEGAL_ENTITY_ATTACHMENT(legalUniqueId, attachmentUniqueId),
  );

  if (response.status !== 200) {
    return null;
  }

  return response.data.url;
};

const deleteLegalEntityAttachment = async (
  legalUniqueId: string,
  attachmentUniqueId: string,
): Promise<any> => {
  const res = await apiClient.delete(
    ApiEndpoints.DELETE_LEGAL_ENTITY_ATTACHMENT(legalUniqueId, attachmentUniqueId),
  );

  if (isResponseError(res.data)) {
    return null;
  }
  return res;
};

export {
  downloadAttachment,
  deleteAttachment,
  addAttachment,
  addAncillaryAttachment,
  addLegalEntityAttachment,
  previewAttachment,
  previewAncillaryAttachment,
  downloadAncillaryAttachment,
  deleteAncillaryAttachment,
  previewLegalEntityAttachment,
  downloadLegalEntityAttachment,
  deleteLegalEntityAttachment,
};
